/**
 * BrandIcon
 *
 * Inline SVG variant of the GroovLens app icon. Renders the vinyl tile as
 * vector graphics so we can tweak the color scheme without having to
 * regenerate PNGs.
 *
 * Variants:
 *   - "teal-disc" → BLACK tile · full TEAL vinyl body · darker-teal grooves
 *                   · WHITE label offset to the right · BLACK spindle hole
 *                   at the disc's true geometric center. Currently trialing.
 *   - "white"     → TEAL tile · WHITE vinyl · teal grooves (matches
 *                   /icons/icon-192.png shipped in the manifest).
 *   - "black"     → TEAL tile · BLACK vinyl · white grooves. (earlier trial)
 *
 * The default is intentionally the variant currently being trialed in the
 * app so a single `size` prop swap doesn't require touching every call
 * site again.
 */
export default function BrandIcon({
  size = 32,
  variant = 'teal-disc',
  showTile = true,
  className = '',
  ...rest
}) {
  // Colors per-variant. Named locally so the JSX below stays readable.
  let tileFill;         // outer rounded-square tile
  let vinylFill;        // vinyl disc body
  let grooveStroke;     // concentric groove color
  let labelFill;        // center label paper color
  let holeFill;         // spindle hole
  let labelCx = 96;     // label center x (96 = disc center; higher = shifted right)

  if (variant === 'teal-disc') {
    tileFill    = '#0B0F14';               // near-black app background
    vinylFill   = 'hsl(174 78% 45%)';      // brand teal
    grooveStroke = 'hsl(174 78% 30%)';     // darker teal, sits ON the vinyl
    labelFill   = '#FFFFFF';
    holeFill    = '#0B0F14';
    labelCx     = 96;                      // centered on the disc
  } else if (variant === 'black') {
    tileFill    = 'hsl(174 78% 45%)';
    vinylFill   = '#0A0A0A';
    grooveStroke = '#FFFFFF';
    labelFill   = 'hsl(174 78% 45%)';
    holeFill    = '#FFFFFF';
  } else {
    // "white" — original manifest icon
    tileFill    = 'hsl(174 78% 45%)';
    vinylFill   = '#FFFFFF';
    grooveStroke = 'hsl(174 78% 45%)';
    labelFill   = 'hsl(174 78% 45%)';
    holeFill    = '#0A0A0A';
  }

  // Concentric groove radii (as % of the 96 center).
  const grooves =
    variant === 'teal-disc'
      ? [68, 60, 52, 44, 36]     // fewer, wider spacing per the reference
      : [66, 60, 54, 48, 42, 36, 30, 24];

  const labelR = variant === 'teal-disc' ? 26 : 14;
  const holeR  = variant === 'teal-disc' ? 5  : 2.5;

  return (
    <svg
      viewBox="0 0 192 192"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label="GroovLens"
      {...rest}
    >
      {/* Tile background — hidden when the caller only wants the naked
          vinyl disc (e.g. the promo cover scene where a black drop-shadow
          provides the elevation instead of an app-tile frame). */}
      {showTile && (
        <rect x="0" y="0" width="192" height="192" rx="34" fill={tileFill} />
      )}

      {/* Vinyl body */}
      <circle cx="96" cy="96" r="76" fill={vinylFill} />

      {/* Concentric grooves — thin strokes, no fill. */}
      {grooves.map((r) => (
        <circle
          key={r}
          cx="96"
          cy="96"
          r={r}
          fill="none"
          stroke={grooveStroke}
          strokeWidth={variant === 'teal-disc' ? 3.5 : 1.4}
          opacity={variant === 'teal-disc' ? 1 : 0.55}
        />
      ))}

      {/* Center label — for "teal-disc" this is offset to the right of the
          disc center to give the mark its distinctive asymmetric identity.
          For the other variants the label stays concentric. */}
      <circle cx={labelCx} cy="96" r={labelR} fill={labelFill} />

      {/* Spindle hole — always at the disc's TRUE geometric center (96,96)
          because a real vinyl's hole must be dead-center regardless of
          where the paper label sits. */}
      <circle cx="96" cy="96" r={holeR} fill={holeFill} />
    </svg>
  );
}
